import type { UntypedFormGroup } from '@angular/forms';

import type { Observable, Subscription } from 'rxjs';
import { BehaviorSubject, Subject } from 'rxjs';

import { trimmedFormValue } from '@core/integrations-util';

import type { Account } from '../../../client/integrations-client.types';
import type {
  AnyDynamicInput,
  DynamicFormConfig,
  DynamicGroupConfig,
  DynamicInputConfig,
} from '../../types';
import { DynamicInputConfigType } from '../../types';
import type { DynamicFormGroup } from './dynamic-form-group/dynamic-form-group';
import { buildFormGroup, buildInput } from './dynamic-form-group/dynamic-form-group';

export class DynamicForm {
  public config: DynamicFormConfig;
  public inputs: AnyDynamicInput[];
  public inputs$: BehaviorSubject<AnyDynamicInput[]>;
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public group: UntypedFormGroup;

  private _valueChangesSubscription: Subscription;
  private _valueChanges = new Subject<any>();

  constructor(config: DynamicFormConfig) {
    this.init(config);
  }

  public init(config: DynamicFormConfig): void {
    this.inputs = config.map(buildInput);
    this.inputs$ = new BehaviorSubject(this.inputs);
    this.group = buildFormGroup(this.inputs);
    if (this._valueChangesSubscription) this._valueChangesSubscription.unsubscribe();
    this._valueChangesSubscription = this.group.valueChanges.subscribe(this._valueChanges);
  }

  public get value(): {} {
    const val = trimmedFormValue(this.group.value);

    return this.inputs.reduce(
      (acc, input) => {
        switch (input.type) {
          // TODO in the future rewrite to send whole admins to back-end
          case DynamicInputConfigType.ADMINS: {
            acc[input.config.name] = val[input.config.name].map((a: Account['admin']) => a.id);
            break;
          }
          default: {
            if ('name' in input.config) acc[input.config.name] = val[input.config.name];
          }
        }
        return acc;
      },
      <any>{},
    );
  }

  public get valid(): boolean {
    if (!this.hasEditableInputs(this.inputs)) {
      return false;
    }
    return this.group.valid;
  }

  public get valueChanges(): Observable<any> {
    return this._valueChanges.asObservable();
  }

  public addAdditionalGroup(inputConfig: DynamicInputConfig): void {
    if (inputConfig.type === DynamicInputConfigType.GROUP) {
      this.addGroup(inputConfig);
    } else {
      this.addInput(inputConfig);
    }
  }

  public addInput(inputConfig: DynamicInputConfig): void {
    const input = buildInput(inputConfig);
    if ('control' in input && 'name' in inputConfig) {
      this.group.addControl(inputConfig.name, input.control);
    }

    this.inputs.push(input);
    this.inputs$.next(this.inputs);
  }

  public addGroup(groupConfig: DynamicGroupConfig): void {
    const input = <DynamicFormGroup>buildInput(groupConfig);
    const group = buildFormGroup(input.inputs);
    this.group.addControl(groupConfig.name, group);
    this.inputs.push(input);
    this.inputs$.next(this.inputs);
  }

  public removeGroup(name: string): void {
    this.group.removeControl(name);

    this.inputs = this.inputs.filter(input => {
      if ('name' in input.config) {
        return input.config.name !== name;
      } else {
        return true;
      }
    });

    this.inputs$.next(this.inputs);
  }

  public triggerValidation(): void {
    if (!this.group) return;

    for (const controlName in this.group.controls) {
      this.group.controls[controlName].updateValueAndValidity();
    }
  }

  // Disable the form when there are no editable inputs
  // Informational inputs only mean the form is not submittable
  private hasEditableInputs(inputs: AnyDynamicInput[]): boolean {
    return inputs.some(input => {
      if (input.kind === 'DynamicFormInput') {
        return true;
      }
      if (input.kind === 'DynamicFormGroup') {
        return this.hasEditableInputs(input.inputs);
      }
      return false;
    });
  }
}
